// Self-paced courses I/O (docs/product/prd/self-paced-courses-prd.md §9, §10).
//
// A self-paced course is bought with Stars from the family wallet (D-SPC-11) and taken alone in
// Creative Code Studio. The server owns every rule: who owns what, which lesson is open, which
// steps are still hidden. This file only calls it.

import { api, ApiError } from '@/lib/api';

export type SelfPacedLessonState = 'locked' | 'open' | 'done';

/**
 * The words of a course page (commerce PRD §4.1a), from curriculum. Null when a course has none
 * yet. Per lesson: minutes, what the student does and what they show at the end.
 */
export interface SelfPacedSalesPage {
  cover_url?: string;
  poster_url?: string;
  hero_video_url?: string;
  demo_play_url?: string;
  reference_label?: string;
  lesson_images?: Record<string, string>;
  reference_assets?: Array<{ title: string; url: string; alt: string }>;
  promise: string;
  you_will_make: string;
  skills: Array<{ title: string; detail: string }>;
  how_it_works: string[];
  you_need: string[];
  faq: Array<{ q: string; a: string }>;
  lessons: Record<string, { minutes?: number; you_will_do: string[]; you_will_show: string[] }>;
}

/** What a course page shows before anyone buys: no steps, no hints, no narration. */
export interface SelfPacedCatalogEntry {
  product_id: string;
  /** Null disables Stars purchase. A positive AUD price can enable direct payment. */
  price_stars: number | null;
  price_aud_cents: number | null;
  slug: string;
  title: string;
  description: string;
  target_age_min: number;
  target_age_max: number;
  lessons: Array<{
    slug: string;
    title: string;
    focus: string | null;
    ai_skill?: string | null;
    deliverable: string | null;
    order_index: number;
  }>;
  sales_page?: SelfPacedSalesPage | null;
}

export interface MySelfPacedCourseSummary {
  entitlement_id: string;
  product_id: string;
  slug: string;
  title: string;
  description: string;
  lessons_total: number;
  lessons_done: number;
}

export interface MySelfPacedCourse {
  id: string;
  slug: string;
  title: string;
  description: string;
  target_age_min?: number;
  target_age_max?: number;
  sales_page?: SelfPacedSalesPage | null;
  lessons: Array<{
    id: string;
    slug: string;
    title: string;
    order_index: number;
    focus?: string | null;
    ai_skill?: string | null;
    deliverable?: string | null;
    state: SelfPacedLessonState;
    missions: Array<{
      id: string;
      slug: string;
      title: string;
      /** The student's own newest project for this lesson, if they started one. */
      project: { id: string; status: string } | null;
    }>;
  }>;
}

export interface SelfPacedPurchaseResult {
  entitlement_id: string;
  stars_paid: number;
  balance_after: number;
}

/** One kid's course in the family progress list (parent view). */
export interface FamilySelfPacedProgress {
  entitlement_id: string;
  status: string;
  kid: { id: string; nickname: string };
  course: { id: string; slug: string; title: string };
  lessons: Array<{ slug: string; title: string; state: SelfPacedLessonState }>;
}

export interface LessonSubmitResult {
  ok: boolean;
  /** Present when `ok` is false: what is still needed. */
  missing?: string[];
  reason?: string;
  stars_awarded?: number;
  already_accepted?: boolean;
}

export const selfPacedKeys = {
  catalog: ['self-paced', 'catalog'] as const,
  mine: ['self-paced', 'mine'] as const,
  course: (slug: string) => ['self-paced', 'course', slug] as const,
};

export function listSelfPacedCatalog(): Promise<SelfPacedCatalogEntry[]> {
  return api<SelfPacedCatalogEntry[]>('/self-paced/catalog');
}

export function listMySelfPacedCourses(): Promise<MySelfPacedCourseSummary[]> {
  return api<MySelfPacedCourseSummary[]>('/self-paced/me/courses');
}

export function getMySelfPacedCourse(slug: string): Promise<MySelfPacedCourse> {
  return api<MySelfPacedCourse>(`/self-paced/me/courses/${encodeURIComponent(slug)}`);
}

/** Parent view: every course the family's kids own, with lesson states. */
export function getFamilySelfPacedProgress(familyId: string): Promise<FamilySelfPacedProgress[]> {
  return api<FamilySelfPacedProgress[]>(`/self-paced/families/${familyId}/progress`);
}

/** A managed kid asks a parent to top up the family wallet. */
export function askParentToTopUp(
  shortfallStars: number,
  reason: 'out_of_stars' | 'course' = 'out_of_stars',
): Promise<{ id: string; status: string }> {
  return api<{ id: string; status: string }>('/approvals', {
    method: 'POST',
    body: { type: 'top_up_request', payload: { shortfall_stars: shortfallStars, reason } },
  });
}

/**
 * A signed-in kid unlocks a course for themselves with the family's Stars. No parent step: the
 * wallet's balance, caps and pause switch are the safety net, and the parent gets an information
 * email afterwards (owner decision 2026-10-12).
 */
export function purchaseSelfPacedCourseAsKid(productId: string): Promise<SelfPacedPurchaseResult> {
  return api<SelfPacedPurchaseResult>('/self-paced/me/purchases', {
    method: 'POST',
    body: { product_id: productId },
  });
}

/** A parent or an independent student buys a course for one child with the family's Stars. */
export function purchaseSelfPacedCourse(
  productId: string,
  kidId: string,
): Promise<SelfPacedPurchaseResult> {
  return api<SelfPacedPurchaseResult>('/self-paced/purchases', {
    method: 'POST',
    body: { product_id: productId, kid_id: kidId },
  });
}

/**
 * Start a lesson: a new Creative Code Studio project linked to the lesson's mission. From lesson 2
 * on the server copies the previous lesson's game into it (D-SPC-10).
 */
export function startSelfPacedLesson(args: {
  title: string;
  missionId: string;
  kidId: string;
  familyId: string;
}): Promise<{ id: string }> {
  return api<{ id: string }>('/projects', {
    method: 'POST',
    body: {
      title: args.title,
      product_line: 'line_b_coding',
      kind: 'game',
      mission_id: args.missionId,
      kid_id: args.kidId,
      family_id: args.familyId,
    },
  });
}

/** Hand the lesson in. The server accepts it only when every step is ticked. */
export function submitSelfPacedLesson(
  projectId: string,
  missionId: string,
): Promise<LessonSubmitResult> {
  return api<LessonSubmitResult>(`/projects/${projectId}/submit`, {
    method: 'POST',
    body: { mission_id: missionId },
  });
}

/** Plain-language reason for a failed purchase or start. Never shows a raw error code. */
export function selfPacedErrorMessage(code: string | undefined, fallback: string): string {
  switch (code) {
    case 'WALLET_INSUFFICIENT':
      // Only for a balance refusal; a spending limit has its own words (see walletRefusal).
      return 'Not enough Stars yet.';
    case 'FAMILY_REQUIRED':
      return 'Courses are not available in workshop sessions.';
    case 'FAMILY_PAUSED':
      return 'The family wallet is paused. Ask a parent.';
    case 'ALREADY_OWNED':
      return 'You already have this course.';
    case 'PRODUCT_NOT_PURCHASABLE':
      return 'This course is not available right now.';
    case 'SELF_PACED_NOT_ENTITLED':
      return 'Unlock this course first.';
    case 'SELF_PACED_LESSON_LOCKED':
      return 'Finish the lesson before this one first.';
    case 'PROJECT_FORK_FAILED':
      return "We couldn't open your game from the last lesson just now. Please try again.";
    default:
      return fallback;
  }
}

/**
 * Why the family wallet refused a purchase. The backend uses one code, WALLET_INSUFFICIENT, for two
 * different problems with different fixes:
 * - `short`: the balance is too low. Topping up fixes it, so a kid can ask a parent to top up.
 * - `limit`: a daily, weekly or monthly spending limit a parent set. Topping up does NOT fix it; only
 *   a parent changing the limit does. Never offer a top-up for it (review 2026-10-12).
 * Returns null for any other error.
 */
export type WalletRefusal = { kind: 'short'; shortfall: number } | { kind: 'limit' };

export function walletRefusal(error: unknown, priceStars: number | null): WalletRefusal | null {
  if (!(error instanceof ApiError) || error.code !== 'WALLET_INSUFFICIENT') return null;
  const details = (error.details ?? {}) as { kind?: string; balance?: number; cost?: number };
  if (details.kind && details.kind !== 'balance') return { kind: 'limit' };
  const cost = details.cost ?? priceStars ?? 0;
  return { kind: 'short', shortfall: Math.max(0, cost - (details.balance ?? 0)) };
}

/** Plain words for a wallet refusal, for a child (Learn) or a grown-up (Portal). */
export function walletRefusalMessage(refusal: WalletRefusal, audience: 'kid' | 'grown-up'): string {
  if (refusal.kind === 'short') return 'Not enough Stars yet.';
  return audience === 'kid'
    ? "Your family's spending limit stops this today. Ask a parent to change it."
    : 'Your family spending limit stops this purchase. You can change the limit in Wallet.';
}
